import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';

// Creación del contexto
const AudioContext = createContext();

// Hook personalizado para usar el contexto
export const useAudio = () => useContext(AudioContext);

// Proveedor del contexto
export const AudioProvider = ({ children }) => {
  const [isMusicPlaying, setIsMusicPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(0.5);
  const [isAudioInitialized, setIsAudioInitialized] = useState(false);
  const [audioReady, setAudioReady] = useState(false);
  const [audioError, setAudioError] = useState(null);
  const backgroundMusicRef = useRef(null);
  const audioElementsRef = useRef({});
  const initializationAttempts = useRef(0);
  const audioContextRef = useRef(null);

  // Función para crear y activar el contexto de audio del navegador
  const createAudioContext = useCallback(() => {
    if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
      try {
        console.log('Creando nuevo contexto de audio...');
        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        audioContextRef.current = new AudioContextClass();
        
        // Intentar reanudar el contexto inmediatamente
        if (audioContextRef.current.state === 'suspended') {
          audioContextRef.current.resume().then(() => {
            console.log('Contexto de audio reanudado exitosamente');
          }).catch(err => {
            console.warn('No se pudo reanudar el contexto de audio:', err);
          });
        }
        
        return audioContextRef.current;
      } catch (error) {
        console.error('Error al crear el contexto de audio:', error);
        setAudioError('No se pudo crear el contexto de audio');
        return null;
      }
    }
    return audioContextRef.current;
  }, []);

  // Inicializar el audio cuando el componente se monta
  useEffect(() => {
    console.log('Inicializando sistema de audio...');
    
    // Crear el contexto de audio
    createAudioContext();
    
    // Precargar la música de fondo
    const audio = new Audio();
    audio.src = '/assets/sounds/musigame.wav';
    audio.loop = true;
    audio.volume = volume;
    audio.preload = 'auto';
    
    // Evento para detectar cuando el audio está realmente cargado
    audio.addEventListener('canplaythrough', () => {
      console.log('Audio cargado y listo para reproducirse');
      setAudioReady(true);
    });
    
    // Evento para detectar errores de carga
    audio.addEventListener('error', (e) => {
      console.error('Error al cargar el audio:', e);
      setAudioError(`Error al cargar el audio: ${e.target.error?.message || 'Error desconocido'}`);
    });
    
    backgroundMusicRef.current = audio;

    // Limpiar al desmontar
    return () => {
      if (backgroundMusicRef.current) {
        backgroundMusicRef.current.pause();
        backgroundMusicRef.current.src = '';
      }
      
      // Limpiar los efectos de sonido precargados
      Object.values(audioElementsRef.current).forEach(audio => {
        audio.src = '';
      });
      
      // No cerrar el contexto de audio al desmontar para evitar errores
      // Solo suspenderlo si está activo
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.suspend().catch(err => {
          console.warn('Error al suspender el contexto de audio:', err);
        });
      }
    };
  }, [volume, createAudioContext]);

  // Actualizar el volumen cuando cambia
  useEffect(() => {
    if (backgroundMusicRef.current) {
      backgroundMusicRef.current.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);

  // Función para cargar y reproducir la música de fondo
  const playMusic = useCallback(() => {
    if (!backgroundMusicRef.current) {
      console.log('Creando nuevo elemento de audio para la música de fondo');
      const audio = new Audio();
      audio.loop = true;
      audio.volume = isMuted ? 0 : volume;
      
      // Probar primero con la ruta en /assets/sounds
      audio.src = '/assets/sounds/musigame.wav';
      
      // Si hay un error, intentar con la ruta alternativa
      audio.onerror = () => {
        console.log('Error al cargar audio desde /assets/sounds, intentando ruta alternativa...');
        audio.src = '/assets/audio/musigame.wav';
        
        // Si también falla la ruta alternativa
        audio.onerror = (e) => {
          console.error('Error al cargar la música de fondo desde ambas rutas:', e);
          setAudioError('No se pudo cargar la música de fondo');
        };
      };
      
      backgroundMusicRef.current = audio;
    }
    
    console.log('Intentando reproducir música de fondo...');
    
    // Si el audio no está inicializado, intentar inicializarlo primero
    if (!isAudioInitialized) {
      console.log('Audio no inicializado, intentando activar el contexto de audio...');
      // Crear un nuevo contexto de audio si el actual está cerrado
      createAudioContext();
    }
    
    try {
      // Asegurarse de que el volumen esté correctamente configurado
      backgroundMusicRef.current.volume = isMuted ? 0 : volume;
      
      // Intentar reproducir la música
      const playPromise = backgroundMusicRef.current.play();
      
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            console.log('Música reproducida exitosamente');
            setIsMusicPlaying(true);
            setAudioError(null);
            setIsAudioInitialized(true);
            setAudioReady(true);
          })
          .catch(error => {
            console.error('Error al reproducir música:', error.message);
            
            // Si el error es por falta de interacción, configuramos un mensaje más claro
            if (error.name === 'NotAllowedError') {
              console.warn('La reproducción automática está bloqueada. Interactúa con la página para activar el audio.');
              setAudioError('Haz clic en la página para activar el audio');
            } else {
              setAudioError(`Error al reproducir música: ${error.message}`);
            }
          });
      }
    } catch (error) {
      console.error('Error al reproducir música:', error);
      setIsMusicPlaying(false);
      setAudioError(`Error al reproducir música: ${error.message}`);
    }
  }, [isAudioInitialized, volume, isMuted, createAudioContext, setAudioError]);

  // Función para reproducir una música personalizada
  const playCustomMusic = useCallback((audioPath) => {
    console.log(`Reproduciendo música personalizada: ${audioPath}`);
    
    // Detener la música actual si está sonando
    if (backgroundMusicRef.current) {
      backgroundMusicRef.current.pause();
      backgroundMusicRef.current.currentTime = 0;
    }

    // Verificar si el archivo existe
    fetch(audioPath, { method: 'HEAD' })
      .then(response => {
        if (response.ok) {
          // Crear un nuevo elemento de audio con la música personalizada
          const audio = new Audio();
          audio.src = audioPath;
          audio.loop = true;
          audio.volume = isMuted ? 0 : volume;
          audio.preload = 'auto';
          
          // Asignar el nuevo audio como música de fondo
          backgroundMusicRef.current = audio;
          
          // Reproducir la nueva música
          try {
            const playPromise = audio.play();
            
            if (playPromise !== undefined) {
              playPromise
                .then(() => {
                  console.log('Música personalizada reproducida exitosamente');
                  setIsMusicPlaying(true);
                  setAudioError(null);
                })
                .catch(error => {
                  console.error('Error al reproducir música personalizada:', error.message);
                  
                  // Si el error es por falta de interacción, intentamos nuevamente después de un retraso
                  if (error.name === 'NotAllowedError') {
                    setAudioError('Haz clic en la página para activar el audio');
                    setTimeout(() => {
                      playCustomMusic(audioPath);
                    }, 1000);
                  } else {
                    setAudioError(`Error al reproducir música personalizada: ${error.message}`);
                  }
                });
            }
          } catch (error) {
            console.error('Error al reproducir música personalizada:', error);
            setIsMusicPlaying(false);
            setAudioError(`Error al reproducir música personalizada: ${error.message}`);
          }
        } else {
          console.warn(`El archivo de música personalizada ${audioPath} no existe o está vacío`);
          setAudioError(`El archivo de música personalizada no existe o está vacío`);
        }
      })
      .catch(error => {
        console.error(`Error al verificar el archivo de música personalizada ${audioPath}:`, error);
        setAudioError(`Error al verificar el archivo de música personalizada: ${error.message}`);
      });
  }, [volume, isMuted]);

  // Función para detener la música
  const stopMusic = useCallback(() => {
    console.log('Deteniendo música...');
    
    try {
      if (backgroundMusicRef.current) {
        // Guardar una referencia al elemento de audio actual
        const currentAudio = backgroundMusicRef.current;
        
        // Pausar la reproducción
        currentAudio.pause();
        
        // Reiniciar la posición de reproducción
        currentAudio.currentTime = 0;
        
        // Desconectar cualquier evento
        currentAudio.onended = null;
        currentAudio.onplay = null;
        currentAudio.onpause = null;
        
        // Actualizar el estado
        setIsMusicPlaying(false);
        
        console.log('Música detenida correctamente');
      } else {
        console.log('No hay música reproduciéndose para detener');
      }
    } catch (error) {
      console.error('Error al detener la música:', error);
    }
  }, []);

  const pauseMusic = useCallback(() => {
    if (backgroundMusicRef.current && isMusicPlaying) {
      backgroundMusicRef.current.pause();
      setIsMusicPlaying(false);
    }
  }, [isMusicPlaying]);

  const toggleMusic = useCallback(() => {
    console.log('Alternando música, estado actual:', isMusicPlaying);
    if (isMusicPlaying) {
      pauseMusic();
    } else {
      playMusic();
    }
  }, [isMusicPlaying, pauseMusic, playMusic]);

  const toggleMute = useCallback(() => {
    setIsMuted(!isMuted);
  }, [isMuted]);

  const adjustVolume = useCallback((newVolume) => {
    const clampedVolume = Math.max(0, Math.min(1, newVolume));
    setVolume(clampedVolume);
  }, []);

  // Función para reproducir efectos de sonido
  const playSound = useCallback((soundName) => {
    if (isMuted) {
      console.log(`No se reproduce el sonido ${soundName} porque el audio está silenciado`);
      return;
    }
    
    console.log(`Intentando reproducir efecto de sonido: ${soundName}`);
    
    try {
      // Verificar si el archivo existe antes de intentar reproducirlo
      fetch(`/assets/sounds/${soundName}.mp3`, { method: 'HEAD' })
        .then(response => {
          if (!response.ok) {
            console.warn(`El archivo de sonido ${soundName}.mp3 no existe o está vacío`);
            return;
          }
          
          // Verificar el tamaño del archivo
          const contentLength = response.headers.get('content-length');
          if (!contentLength || parseInt(contentLength) <= 100) {
            console.warn(`El archivo de sonido ${soundName}.mp3 está vacío o es demasiado pequeño (${contentLength} bytes)`);
            return;
          }
          
          // Crear un nuevo elemento de audio para cada reproducción
          const sound = new Audio(`/assets/sounds/${soundName}.mp3`);
          sound.volume = volume;
          
          // Asegurarse de que el contexto de audio esté activo
          if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
            audioContextRef.current.resume().then(() => {
              console.log('Contexto de audio reanudado para reproducir sonido');
              sound.play().catch(error => {
                console.warn(`Error al reproducir el sonido ${soundName}:`, error.message);
              });
            });
          } else {
            sound.play().catch(error => {
              console.warn(`Error al reproducir el sonido ${soundName}:`, error.message);
            });
          }
        })
        .catch(error => {
          console.warn(`Error al verificar el archivo de sonido ${soundName}:`, error);
        });
    } catch (error) {
      console.warn(`Error al reproducir el sonido ${soundName}:`, error);
    }
  }, [isMuted, volume]);

  // Precargar efectos de sonido comunes para mejorar la respuesta
  useEffect(() => {
    const commonSounds = ['button_click', 'game_start', 'correct_answer', 'wrong_answer'];
    
    commonSounds.forEach(soundName => {
      try {
        const audio = new Audio(`/assets/sounds/${soundName}.mp3`);
        audio.preload = 'auto';
        audioElementsRef.current[soundName] = audio;
        
        // Evento para detectar cuando el audio está cargado
        audio.addEventListener('canplaythrough', () => {
          console.log(`Sonido ${soundName} precargado correctamente`);
        });
        
        // Evento para detectar errores de carga
        audio.addEventListener('error', (e) => {
          console.warn(`Error al precargar el sonido ${soundName}:`, e);
        });
      } catch (error) {
        console.warn(`Error al precargar el sonido ${soundName}:`, error);
      }
    });
    
    // Limpiar al desmontar
    return () => {
      Object.values(audioElementsRef.current).forEach(audio => {
        audio.src = '';
      });
    };
  }, []);

  return (
    <AudioContext.Provider value={{
      isMusicPlaying,
      isMuted,
      volume,
      playMusic,
      playCustomMusic,
      stopMusic,
      pauseMusic,
      toggleMusic,
      toggleMute,
      adjustVolume,
      playSound,
      isAudioInitialized,
      audioReady,
      audioError
    }}>
      {children}
    </AudioContext.Provider>
  );
};

export default AudioContext;
